import { Text, Textarea, VStack } from "@chakra-ui/react";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { ConfirmationDialog } from "@/common/components/ConfirmationDialog";
import { FormRootError } from "@/common/components/Form/FormRootError";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useUpdateCompanyFlagAccessMutation } from "@/company/data/useUpdateCompanyFeatureAccessMutation";
import { CompanyDetail } from "@reflag/shared/companyAPI";
import { EntityFlagDTO, UpdateEntityFlagsBody } from "@reflag/shared/flagAPI";

type CompanyFlagToggleDialogProps = {
  company: Pick<CompanyDetail, "id" | "name">;
  flag: Pick<EntityFlagDTO, "key" | "name" | "specificTargetValue">;
  isOpen: boolean;
  onClose: () => void;
};

export const CompanyFlagToggleDialog = ({
  company,
  flag,
  isOpen,
  onClose,
}: CompanyFlagToggleDialogProps) => {
  const { env } = useEnvironment();
  const isExplicitlyTargeted = flag.specificTargetValue === true;
  const companyName = company.name || company.id;

  const placeholder = useCallback(
    (tense: "past" | "future" = "past", lowerCase = false) => {
      let access = !isExplicitlyTargeted
        ? tense === "past"
          ? "Granted"
          : "Grant"
        : tense === "past"
          ? "Revoked"
          : "Revoke";
      access = lowerCase ? access.toLowerCase() : access;
      return `${access} company ${companyName} access to flag ${flag.name} in ${env.name}.`;
    },
    [companyName, env.name, flag.name, isExplicitlyTargeted],
  );

  const updateFlagAccessMutation = useUpdateCompanyFlagAccessMutation(
    company.id,
  );

  const form = useForm({
    defaultValues: {
      changeDescription: "",
    },
  });

  const onConfirmClick = useFormMutationSubmitHandler(
    form,
    updateFlagAccessMutation,
    () => {
      form.resetField("changeDescription");
      onClose?.();
    },
    {
      prepareVariables({ changeDescription }) {
        return {
          updates: [
            {
              flagKey: flag.key,
              specificTargetValue: isExplicitlyTargeted ? null : true,
            },
          ],
          changeDescription: changeDescription || placeholder("past"),
        } satisfies UpdateEntityFlagsBody;
      },
      successToast: placeholder("past"),
      errorToast: `Failed to ${placeholder("future", true)}`,
    },
  );

  return (
    <FormProvider {...form}>
      <ConfirmationDialog
        _contentProps={{ maxW: "2xl" }}
        description={
          <VStack as="form" justify="flex-start" spacing={4} w="full">
            <ManagedFormControl
              name="changeDescription"
              render={({ field }) => (
                <Textarea placeholder={placeholder("past")} {...field} />
              )}
            />
            <FormRootError />
          </VStack>
        }
        isLoading={updateFlagAccessMutation.isPending}
        isOpen={isOpen}
        title={
          <Text whiteSpace="nowrap">
            {!isExplicitlyTargeted ? "Grant" : "Revoke"} flag access
          </Text>
        }
        onCancel={() => {
          form.resetField("changeDescription");
          onClose?.();
        }}
        onClose={onClose}
        onConfirm={onConfirmClick}
      />
    </FormProvider>
  );
};
